import type { BuildingCategory, BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import shopUtils from '@/utils/shop';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useConfigStore } from './configStore';
import type { TFunction } from 'i18next';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { ShopState, ShopActions, ShopFilterConfig } from '@/stores/types/shopStore';
import type { PoiContentSlide } from '@/types/shop';
import { DUTY_FREE_BUILDING_ID, isDutyFreePoi } from '@/utils/duty-free';
import { compareByKioskBuilding, resolveKioskPriorityBuilding } from '@/utils/kiosk-building-priority';

const resolveDefaultBuildingId = (buildings: BuildingInStoreInfo[] | undefined, language: string): string => {
    if (!buildings?.length) return 'all';
    if (buildings.length > 1) return 'all';

    const shopPois = buildings.flatMap((building) => building.filterPoiContents.shop ?? []);
    if (shopPois.some((poi) => isDutyFreePoi(poi, language))) return 'all';

    return buildings[0].id;
};

interface ShopStore extends ShopState, ShopActions { }

export const useShopStore = create<ShopStore>()(
    devtools(
        (set, get) => ({
            // Initial state
            shopContents: [],
            rawShopContents: [],
            searchText: '',
            selectedFloor: {
                id: 'all',
                label: '',
                value: '',
                data: null,
            },
            selectedBuildingId: 'all',
            selectedCategory: null,
            t: null,
            currentMode: SHOP_SEARCH_MODE.SEARCH_BY_TEXT,
            keyboardLanguage: 'ko',

            setKeyboardLanguage: (language: 'ko' | 'en') => {
                const prev = get().keyboardLanguage;
                if (prev === language) return;

                set({ keyboardLanguage: language });
            },

            setCurrentMode: (mode: SHOP_SEARCH_MODE) => {
                const prev = get().currentMode;
                if (prev === mode) return;

                set({ currentMode: mode });
            },

            setSearchText: (text: string) => {
                const prev = get().searchText;
                if (prev === text) return;

                set({ searchText: text });
            },

            setSelectedFloor: (floor: { id: string; label: string; value: string; data: FloorInfo | null | string[] } | null) => {
                const prev = get().selectedFloor;
                if (prev?.id === floor?.id) return;

                set({ selectedFloor: floor });
            },

            setSelectedBuildingId: (buildingId: string | null) => {
                const prev = get().selectedBuildingId;
                if (prev === buildingId) return;

                set({ selectedBuildingId: buildingId });
            },

            setSelectedCategory: (category: BuildingCategory) => {
                const prev = get().selectedCategory;
                if (prev?.clientCode === category?.clientCode) return;

                set({ selectedCategory: category });
            },

            setTranslationFunction: (t: TFunction<'translation', undefined>) => {
                set({ t });
            },

            resetFilters: () => {
                const state = get();
                const { t } = state;

                const { config } = useConfigStore.getState();
                const {
                    mergedCategories: { shop },
                    server,
                } = config;

                const allFloorText = t ? t('shop.search.text.content.all_floor') : 'All Floors';
                const newBuildingId = resolveDefaultBuildingId(server?.store?.buildings, config.ui.language);
                const isMultipleBuildings = newBuildingId === 'all';
                const newCategory = isMultipleBuildings ? shop?.[0] : server?.store?.buildings?.[0]?.filterCategories?.shop?.[0];

                set({
                    searchText: '',
                    selectedFloor: {
                        id: 'all',
                        label: allFloorText,
                        value: 'all',
                        data: null,
                    },
                    selectedBuildingId: newBuildingId,
                    selectedCategory: newCategory,
                });
            },

            updateShopContents: (searchMode: SHOP_SEARCH_MODE): PoiContentSlide[] => {
                const state = get();
                const { selectedFloor, selectedBuildingId, selectedCategory, searchText, keyboardLanguage } = state;
                const { config } = useConfigStore.getState();
                const {
                    ui: { language },
                } = config;

                // Regenerate rawShopContents with searchText and keyboardLanguage to include matching keywords
                const rawShopContents = shopUtils.convertPoiContentToSlide(language, config.server?.store?.buildings ?? [], searchText);

                // Find storeCode and buildingCode from selectedBuildingId
                let storeCode: string | null = null;
                let buildingCode: string | null = null;

                if (selectedBuildingId && selectedBuildingId !== 'all' && selectedBuildingId !== DUTY_FREE_BUILDING_ID) {
                    const selectedBuilding = config.server?.store?.buildings?.find((b) => b.id === selectedBuildingId);
                    if (selectedBuilding) {
                        storeCode = selectedBuilding.storeCode;
                        buildingCode = selectedBuilding.buildingCode;
                    }
                }

                const filterConfig: ShopFilterConfig = {
                    language,
                    selectedBuildingId,
                    storeCode,
                    buildingCode,
                    selectedFloor,
                    selectedCategory,
                    searchText,
                    buildings: config.server?.store?.buildings,
                };

                if (!rawShopContents?.length) {
                    if (state.shopContents?.length > 0) {
                        set({ shopContents: [] });
                    }
                    return [];
                }

                const shopFilter = shopUtils.createShopFilter(searchMode, filterConfig, keyboardLanguage);
                const filteredContents = rawShopContents.filter(shopFilter);

                if (filteredContents?.length === 0) {
                    if (state.shopContents?.length > 0) {
                        set({ shopContents: [] });
                    }
                    return [];
                }

                const priorityBuilding = resolveKioskPriorityBuilding(
                    config.server?.store?.studioMapId,
                    config.server?.building,
                    config.server?.store?.buildings,
                );

                const sortedContents = filteredContents.sort((a, b) => {
                    if (a.isDutyFreePoi && !b.isDutyFreePoi) return 1;
                    if (!a.isDutyFreePoi && b.isDutyFreePoi) return -1;

                    const priorityDiff = compareByKioskBuilding(a, b, priorityBuilding);
                    if (priorityDiff !== 0) return priorityDiff;

                    const nameA = a?.data?.name?.[language] || '';
                    const nameB = b?.data?.name?.[language] || '';
                    return nameA.localeCompare(nameB, language, {
                        numeric: false,
                        sensitivity: 'base',
                        ignorePunctuation: true,
                    });
                });

                set({ shopContents: sortedContents });

                return sortedContents;
            },

            initialize: (t: TFunction<'translation', undefined>) => {
                const state = get();
                if (state.t === t && state.rawShopContents?.length > 0) {
                    return;
                }

                const { config } = useConfigStore.getState();
                const {
                    mergedCategories: { shop },
                    ui: { language },
                    server,
                } = config;

                const rawShopContents = shopUtils.convertPoiContentToSlide(language, server?.store?.buildings ?? []);
                const selectedBuildingId = resolveDefaultBuildingId(server?.store?.buildings, language);
                const updates: Partial<ShopState> = { t, rawShopContents, currentMode: SHOP_SEARCH_MODE.SEARCH_BY_TEXT, selectedBuildingId };
                if (shop && shop?.length > 0 && state.selectedCategory?.clientCode !== shop?.[0]?.clientCode) {
                    updates.selectedCategory = shop?.[0];
                }

                set(updates);
            },
        }),
        {
            name: 'shop-store',
        }
    )
);

let previousBuildings: BuildingInStoreInfo[] | undefined = undefined;
let previousLanguage: string = '';

useConfigStore.subscribe(() => {
    const { config } = useConfigStore.getState();
    const currentBuildings = config.server?.store?.buildings;
    const currentLanguage = config.ui?.language;

    if (currentBuildings !== previousBuildings || currentLanguage !== previousLanguage) {
        previousBuildings = currentBuildings;
        previousLanguage = currentLanguage;

        const { initialize, t } = useShopStore.getState();
        if (t) {
            initialize(t);
        }
    }
});
